<template>
  <div class="donation-background" :style="backgroundCSS">
    <div
      :class="{ 'donation-wrapper-company': isCompany }"
      class="donation-wrapper"
    >
      <div
        class="donation-form__head"
        :style="headCSS"
        :class="{ 'donation-form__disable-hero': !image || collection }"
      >
        <div
          :class="{ 'donation-form__title-nobg': !image || collection }"
          class="donation-form__title"
        >
          <h1>{{ title }}</h1>
        </div>

        <div v-if="subtitle && subtitle !== ''" class="donation-form__subtitle">
          <h2>{{ subtitle }}</h2>
        </div>

        <div
          class="donation-form__body"
          v-if="body && body.length"
          v-html="body"
        />
      </div>

      <div v-if="collection" class="collection-header">
        <img :src="collection.image" />
        <div>
          <p class="collection-header__target">{{ targetName }}</p>
          <h2>{{ collection.title }}</h2>
          <p class="collection-header__author">{{ collection.author }}</p>
          <div class="collection-header__goal">
            <p>Start</p>
            <p v-if="collection.goal > collection.amount">
              {{ collection.goal }}kr
            </p>
            <p v-else class="goal-completed">Mål uppfyllt</p>
          </div>
          <meter
            :value="collection.amount"
            min="0"
            :max="collection.goal"
          ></meter>
        </div>
      </div>

      <div class="donation-form">
        <BankAccount
          v-if="bankAccountModal"
          @close="closeBankAccountModal"
          :label="labels.selectBankAccount"
          :submit="labels.submitBankAccount"
          @select="selectBankAccountFromModal"
          :accounts="bankAccountRetrieval.accounts"
          :header="labels.gettingAccounts"
          :qr="qr"
          :showQr="showQr"
          :qrMessage="labels.qrBankText"
          :qrMessageMobile="labels.qrBankTextMobile"
          :autoStartToken="autoStartToken"
          :sameDevice="labels.sameDeviceBank"
          :anotherDevice="labels.anotherDeviceBank"
          :sameDeviceMobile="labels.sameDeviceBankMobile"
          :anotherDeviceMobile="labels.anotherDeviceBankMobile"
          :bankAccountRetrieval="bankAccountRetrieval"
          :text="bankIdText"
          :modalHeader="bankAccountHeader"
          :modalText="bankAccountText"
          @proceedSameDevice="sameDeviceQr"
          @proceedAnotherDevice="anotherDeviceQr"
        />

        <h1 v-if="secondaryHeading && !collection">{{ secondaryHeading }}</h1>

        <div
          v-if="pledgeTargets && !collection"
          class="donation-form__fields donation-form__targets"
        >
          <div class="donation-form__label">
            <label>{{ labels.choosePledgeTarget }}</label>
          </div>
          <div class="donation-form__fields--field">
            <select
              v-model="form.pledgeTarget"
              :class="{
                'donation-form__form-control-valid':
                  validPledge && isValid('pledgeTarget'),
                'donation-form__form-control-error':
                  validPledge && !isValid('pledgeTarget'),
              }"
            >
              <option selected disabled :value="null">
                {{ labels.pledgeTarget }}
              </option>
              <option
                v-for="target in pledgeTargets"
                :key="target.targetID"
                :value="target.targetID"
              >
                {{ target.targetDescription }}
              </option>
            </select>
          </div>
        </div>

        <div
          v-if="step > 0 && !useCompensation"
          class="donation-form__fields donation-form__amounts"
        >
          <div class="donation-form__label">
            <label>{{ labels.amounts_label }}</label>
          </div>
          <template v-if="amounts.length">
            <div
              v-for="amount in amounts"
              :key="amount"
              @change="
                prefilledAmountChanged();
                stepPassed(1);
                saveUncompletedLead();
              "
              class="donation-form__form-control donation-form__fields--field"
            >
              <input
                :id="makeId('amount-', amount)"
                v-model="prefilled"
                :value="amount"
                type="radio"
              />
              <label :for="makeId('amount-', amount)">{{
                amount | currency(showCurrency, currency)
              }}</label>
            </div>
          </template>
          <div
            @change="
              amountChanged();
              stepPassed(1);
              saveUncompletedLead();
            "
            @click="amountChanged()"
            v-if="!hideCustomAmount"
            class="donation-form__form-amount donation-form__form-control donation-form__fields--field"
          >
            <currency-input
              v-model="amount"
              :placeholder="labels.amount"
              :class="{
                'donation-form__form-control-valid':
                  prefilled === null && isValid('amount'),
                'donation-form__form-control-error':
                  prefilled === null && !isValid('amount'),
              }"
              :currency="currency"
              v-if="showCurrency"
            />
            <input
              v-model="amount"
              :placeholder="labels.amount"
              :class="{
                'donation-form__form-control-valid':
                  prefilled === null && isValid('amount'),
                'donation-form__form-control-error':
                  prefilled === null && !isValid('amount'),
              }"
              type="tel"
              v-else
            />
          </div>
        </div>

        <div
          v-if="step > 0 && useCompensation"
          class="donation-form__compensation"
        >
          <h2>Mil jag vill färdas</h2>

          <div class="donation-form__compensation--wrapper">
            <input
              type="number"
              placeholder="Mil med bil"
              ref="car"
              @input="
                amountChangedCompensation('car', $event.target.value);
                stepPassed(1);
              "
            />

            <p>eller</p>

            <input
              type="number"
              placeholder="Mil med flyg"
              ref="airPlane"
              @input="
                amountChangedCompensation('airplane', $event.target.value);
                stepPassed(1);
              "
            />
          </div>

          <div class="donation-form__compensation--amount">
            {{
              form.amount !== null && !isNaN(form.amount)
                ? Math.round(form.amount) + 'kr'
                : '0kr'
            }}
          </div>
        </div>

        <div
          v-if="step > 0 && hasBothRecurrentAndSimple"
          class="donation-form__fields donation-form__frequency"
        >
          <div class="donation-form__label">
            <label>{{ labels.donation_label }}</label>
          </div>
          <div
            @change="stepPassed(1)"
            :class="{
              'donation-form__fields-pristine': form.paymentType === null,
            }"
            class="donation-form__form-control donation-form__fields--field"
          >
            <input
              v-model="form.paymentType"
              id="type-once"
              value="oneTime"
              type="radio"
            />
            <label for="type-once">{{ labels.oneTime }}</label>
          </div>
          <div
            @change="stepPassed(1)"
            :class="{
              'donation-form__fields-pristine': form.paymentType === null,
            }"
            class="donation-form__form-control donation-form__fields--field"
          >
            <input
              v-model="form.paymentType"
              @change="saveUncompletedLead()"
              id="type-recurrent"
              value="recurrent"
              type="radio"
            />
            <label for="type-recurrent">{{ labels.recurrent }}</label>
          </div>
        </div>

        <div
          v-if="step > 1 && anonym === true"
          class="donation-form__anonymous"
        >
          <input
            @change="anonymStatusChanged"
            type="checkbox"
            id="anonymous"
            v-model="form.anonym"
          />
          <label for="anonymous">
            {{ labels.anonym
            }}<span v-if="hasSwish"> ({{ labels.notWithSwish }})</span>
          </label>
        </div>

        <div
          v-if="step > 1 && gdpr !== '' && form.anonym !== true"
          v-html="gdpr"
          class="donation-form__gdpr"
        />

        <div
          v-if="
            isCompany &&
            step > 1 &&
            form.anonym !== true &&
            availablePaymentMethods.length > 1
          "
          class="donation-form__fields donation-form__company"
        >
          <div class="donation-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            :class="{
              'donation-form__form-control-valid':
                validate && isValid('socialSecurityNumber'),
              'donation-form__form-control-error':
                validate && !isValid('socialSecurityNumber'),
            }"
            class="donation-form__form-control donation-form__fields--field"
          >
            <input
              v-model="form.socialSecurityNumber"
              :placeholder="labels.socialSecurityNumber"
              @change="
                checkValidation('socialSecurityNumber');
                saveUncompletedLead('socialSecurityNumber');
              "
              type="tel"
              name="socialSecurityNumber"
            />
          </div>
          <div class="donation-form__form-control donation-form__fields--field">
            <input
              v-model="form.company"
              :placeholder="labels.company"
              :class="{
                'donation-form__form-control-valid':
                  validate && isValid('company'),
                'donation-form__form-control-error':
                  validate && !isValid('company'),
              }"
              @change="saveUncompletedLead('company')"
              name="company"
            />
          </div>
        </div>
        <div
          v-if="
            step > 1 &&
            form.anonym !== true &&
            availablePaymentMethods.length > 1
          "
          :class="
            isCompany
              ? 'donation-form__mailandtel-company'
              : 'donation-form__mailandtel'
          "
          class="donation-form__fields"
        >
          <div v-if="!isCompany" class="donation-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            class="donation-form__form-control donation-form__fields--field"
            :class="{
              'donation-form__form-control-valid':
                validEmail && isValid('email'),
              'donation-form__form-control-error':
                validEmail && !isValid('email'),
            }"
          >
            <input
              v-model="form.email"
              :placeholder="labels.email"
              :class="{
                'donation-form__form-control-valid':
                  validEmail && isValid('email'),
                'donation-form__form-control-error':
                  validEmail && !isValid('email'),
              }"
              @change="checkValidation('email')"
              type="email"
              name="email"
            />
          </div>
          <div
            class="donation-form__form-control donation-form__fields--field"
            :class="{
              'donation-form__form-control-valid':
                validPhone && isValid('phone'),
              'donation-form__form-control-error':
                validPhone && !isValid('phone'),
            }"
          >
            <input
              v-model="form.phone"
              :placeholder="labels.phone"
              :class="{
                'donation-form__form-control-valid':
                  validPhone && isValid('phone'),
                'donation-form__form-control-error':
                  validPhone && !isValid('phone'),
              }"
              @change="checkValidation('phone')"
              type="tel"
              name="phone"
            />
          </div>
        </div>

        <div id="anchor" />

        <div
          v-if="step > 1 && availablePaymentMethods.length > 1"
          class="donation-form__fields donation-form__methods"
        >
          <div class="donation-form__label">
            <label>{{ labels.payment_label }}</label>
          </div>
          <div
            v-for="method in availablePaymentMethods"
            :key="method"
            @change="
              stepPassed(2);
              saveUncompletedLead();
            "
            :class="{ 'donation-form__fields-pristine': form.method === null }"
            class="donation-form__fields--field"
          >
            <input
              :id="makeId('method-', method)"
              v-model="form.method"
              type="radio"
              :value="method"
            />
            <label
              :for="makeId('method-', method)"
              :class="makeClass(method)"
              >{{ labels[method] }}</label
            >
          </div>
        </div>

        <div id="errors" v-if="errorPositionTop === true">
          <div class="donation-form__error" v-html="error" />
          <div class="donation-form__success" v-html="message" />
        </div>

        <div
          v-if="showForm || showGetAddressWithSsn"
          :class="{ 'donation-form__address-wrapper-company': isCompany }"
          class="donation-form__address-wrapper"
        >
          <div class="donation-form__label">
            <label>{{ labels.contact }}</label>
          </div>
          <div
            v-if="
              step > 1 &&
              form.anonym !== true &&
              availablePaymentMethods.length === 1
            "
            class="donation-form__fields donation-form__person"
          >
            <div
              class="donation-form__form-control donation-form__fields--field"
              :class="{
                'donation-form__form-control-valid-parent':
                  validEmail && isValid('email'),
                'donation-form__form-control-error-parent':
                  validEmail && !isValid('email'),
              }"
            >
              <input
                v-model="form.email"
                :placeholder="labels.email"
                :class="{
                  'donation-form__form-control-valid':
                    validEmail && isValid('email'),
                  'donation-form__form-control-error':
                    validEmail && !isValid('email'),
                }"
                @change="checkValidation('email')"
                type="email"
                name="email"
              />
            </div>
            <div
              class="donation-form__form-control donation-form__fields--field"
              :class="{
                'donation-form__form-control-valid-parent':
                  validPhone && isValid('phone'),
                'donation-form__form-control-error-parent':
                  validPhone && !isValid('phone'),
              }"
            >
              <input
                v-model="form.phone"
                :placeholder="labels.phone"
                :class="{
                  'donation-form__form-control-valid':
                    validPhone && isValid('phone'),
                  'donation-form__form-control-error':
                    validPhone && !isValid('phone'),
                }"
                @change="checkValidation('phone')"
                type="tel"
                name="phone"
              />
            </div>
            <div
              v-if="showForm && isCompany"
              class="donation-form__fields donation-form__company"
            >
              <div
                class="donation-form__form-control donation-form__fields--field"
              >
                <input
                  v-model="form.company"
                  @change="saveUncompletedLead('company')"
                  :placeholder="labels.company"
                  name="company"
                />
              </div>
              <div
                class="donation-form__form-control donation-form__fields--field"
              >
                <input
                  v-model="form.socialSecurityNumber"
                  @change="saveUncompletedLead('socialSecurityNumber')"
                  :placeholder="labels.socialSecurityNumber"
                  type="tel"
                  name="socialSecurityNumber"
                />
              </div>
            </div>
          </div>
          <div
            v-if="showGetAddressWithSsn"
            class="donation-form__fields donation-form__getaddress"
          >
            <div
              :class="[
                {
                  'donation-form__orgnr': isCompany,
                },
                {
                  'donation-form__form-control-valid-parent':
                    validSsn && isValid('socialSecurityNumber'),
                  'donation-form__form-control-error-parent':
                    validSsn && !isValid('socialSecurityNumber'),
                },
              ]"
              class="donation-form__form-control donation-form__fields--field"
            >
              <input
                v-model="form.socialSecurityNumber"
                :placeholder="labels.socialSecurityNumber"
                :class="{
                  'donation-form__form-control-valid':
                    isAutogiro && validSsn && isValid('socialSecurityNumber'),
                  'donation-form__form-control-error':
                    isAutogiro && validSsn && !isValid('socialSecurityNumber'),
                }"
                @change="
                  checkValidation('socialsecuritynumber');
                  saveUncompletedLead('socialsecuritynumber');
                "
                type="tel"
                name="socialsecuritynumber"
              />
            </div>
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <button
                @click.prevent="
                  getOrEmptyAddress();
                  stepPassed(3);
                "
                :class="{
                  'donation-form__button-loading':
                    addressRetrievalInProgress === true,
                }"
                id="bank-button"
              >
                {{ addressLabel }}
              </button>
            </div>
            <div
              v-if="showGetAddressWithSsn && getAddressShowForm === false"
              class="donation-form__form-control donation-form__fields--field"
            >
              <button
                @click="
                  fillSelf();
                  stepPassed(3);
                "
              >
                {{ labels.manualAddress }}
              </button>
            </div>
          </div>

          <div
            v-if="showForm"
            :class="{ 'donation-form__names-company': isCompany }"
            class="donation-form__fields donation-form__names"
          >
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.firstName"
                :placeholder="labels.firstName"
                :class="{
                  'donation-form__form-control-valid':
                    validName && isValid('firstName'),
                  'donation-form__form-control-error':
                    validName && !isValid('firstName'),
                }"
                :masked="hideAddress && maskAddress"
                @change="saveUncompletedLead()"
                name="firstname"
              />
            </div>
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.lastName"
                :placeholder="labels.lastName"
                :class="{
                  'donation-form__form-control-valid':
                    validSurname && isValid('lastName'),
                  'donation-form__form-control-error':
                    validSurname && !isValid('lastName'),
                }"
                :masked="hideAddress && maskAddress"
                @change="saveUncompletedLead()"
                name="lastname"
              />
            </div>
          </div>
          <div
            v-if="showForm"
            :class="{ 'donation-form__address-company': isCompany }"
            class="donation-form__fields donation-form__address"
          >
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.address"
                :placeholder="labels.address"
                @change="saveUncompletedLead('address')"
                :class="{
                  'donation-form__form-control-valid':
                    validAdress && isValid('address'),
                  'donation-form__form-control-error':
                    validAdress && !isValid('address'),
                }"
                :masked="hideAddress && maskAddress"
                name="address"
              />
            </div>
            <div
              v-if="isCompany"
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.phone2"
                @change="saveUncompletedLead()"
                :placeholder="labels.phone2"
                :masked="hideAddress && maskAddress"
                name="phone2"
              />
            </div>
            <div
              v-else
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.address2"
                @change="saveUncompletedLead()"
                :placeholder="labels.address2"
                :masked="hideAddress && maskAddress"
                name="co"
              />
            </div>
          </div>
          <div
            v-if="showForm"
            class="donation-form__fields donation-form__city"
          >
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.zip"
                :placeholder="labels.zip"
                @change="saveUncompletedLead()"
                :class="{
                  'donation-form__form-control-valid':
                    validZip && isValid('zip'),
                  'donation-form__form-control-error':
                    validZip && !isValid('zip'),
                }"
                :masked="hideAddress && maskAddress"
                name="zip"
              />
            </div>
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <maskable-input
                v-model="form.city"
                :placeholder="labels.city"
                @change="saveUncompletedLead()"
                :class="{
                  'donation-form__form-control-valid':
                    validCity && isValid('city'),
                  'donation-form__form-control-error':
                    validCity && !isValid('city'),
                }"
                :masked="hideAddress && maskAddress"
                name="city"
              />
            </div>
          </div>

          <div
            v-if="
              (showForm && isAutogiro && banks.length > 0) ||
              manualFill === true
            "
            class="donation-form__fields donation-form__bank1"
          >
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <select
                v-model="form.bank"
                :class="{
                  'donation-form__form-control-valid':
                    validate && form.bank !== 0,
                  'donation-form__form-control-error':
                    validate && form.bank === 0,
                }"
                @change="saveUncompletedLead('bank')"
                name="bank"
              >
                <option value="0" disabled="disabled" selected>
                  {{ labels.chooseBank }}
                </option>
                <option v-for="bank in banks" :key="bank.id" :value="bank.id">
                  {{ bank.name }}
                </option>
              </select>
            </div>
            <div
              class="donation-form__form-control donation-form__fields--field"
            >
              <button
                @click.prevent="openBankAccountModal"
                :disabled="bankAccountRetrieval.status !== false"
                :class="{
                  'donation-form__button-loading':
                    bankAccountRetrieval.status !== false,
                }"
              >
                {{ labels.getBankAccount }}
              </button>
            </div>
          </div>
          <div
            v-if="
              (showForm && isAutogiro && banks.length > 0) ||
              manualFill === true
            "
            class="donation-form__fields donation-form__bank2"
          >
            <div
              class="donation-form__form-control donation-form__fields--field"
              :class="{
                'donation-form__form-control-valid-parent':
                  validClearing && isValid('bankClearing'),
                'donation-form__form-control-error-parent':
                  validClearing && !isValid('bankClearing'),
              }"
            >
              <input
                v-model="form.bankClearing"
                :placeholder="labels.bankClearing"
                @change="
                  checkValidation('clearing');
                  saveUncompletedLead('clearing');
                "
                :class="{
                  'donation-form__form-control-valid':
                    validClearing && isValid('bankClearing'),
                  'donation-form__form-control-error':
                    validClearing && !isValid('bankClearing'),
                }"
                type="tel"
                name="clearing"
              />
            </div>
            <div
              class="donation-form__form-control donation-form__fields--field"
              :class="{
                'donation-form__form-control-valid-parent':
                  validAccount && isValid('bankAccount'),
                'donation-form__form-control-error-parent':
                  validAccount && !isValid('bankAccount'),
              }"
            >
              <input
                v-model="form.bankAccount"
                :placeholder="labels.bankAccount"
                @change="
                  checkValidation('account');
                  saveUncompletedLead('account');
                "
                :class="{
                  'donation-form__form-control-valid':
                    validAccount && isValid('bankAccount'),
                  'donation-form__form-control-error':
                    validAccount && !isValid('bankAccount'),
                }"
                type="tel"
                name="account"
              />
            </div>
          </div>

          <div
            v-if="isAutogiro && checkboxText && checkboxText !== ''"
            class="donation-form__checkbox"
          >
            <input
              :class="{
                'donation-form__form-control-error':
                  validate && checked === false,
              }"
              type="checkbox"
              v-model="checked"
            />
            <span v-html="checkboxText" />
          </div>

          <div v-if="showForm && terms !== ''" class="donation-form__terms">
            <div
              v-html="showTerms ? terms : $options.filters.preview(terms, 75)"
              class="donation-form__terms-preview"
            />
            <div
              v-if="$options.filters.stripTags(terms).length > 75"
              class="donation-form__terms-toggle"
            >
              <a @click.prevent="toggleTerms" href="#">{{
                showTerms ? labels.showLess : labels.showMore
              }}</a>
            </div>
          </div>
        </div>

        <div id="errors" v-if="errorPositionTop === false">
          <div class="donation-form__error" v-html="error" />
          <div class="donation-form__success" v-html="message" />
        </div>

        <BankId
          v-if="bankIdModal"
          @close="closeBankIdModal"
          :text="labels.qrBankId"
          :header="bankIdHeader"
          :paymentButtonLoading="paymentButtonLoading"
          :sameDevice="labels.sameDevice"
          :anotherDevice="labels.anotherDevice"
          :sameDeviceMobile="labels.sameDeviceMobile"
          :anotherDeviceMobile="labels.anotherDeviceMobile"
          :message="bankIdSignature.message"
          :showMessage="showBankIdMessage"
          :qr="qr"
          @proceedSameDevice="autoStart"
        />

        <div
          v-else-if="showForm || (anonym && form.anonym === true)"
          class="donation-form__form-control donation-form__submit"
        >
          <button
            @click.prevent="submit"
            :disabled="paymentButtonLoading || submitDisabled"
            :class="{
              'donation-form__button-loading': paymentButtonLoading === true,
            }"
          >
            {{ labels.submit }}
          </button>
        </div>

        <div
          id="stripe-wrapper"
          v-if="hasStripe"
          v-show="showStripe"
          class="donation-form__stripe"
        >
          <Stripe
            @completed="finishStripe"
            @initiated="initiateStripe"
            :pk="stripeKey"
            :labels="labels"
            :amount="form.amount"
            :name="fullName"
          />
        </div>

        <Swish
          v-if="hasSwish"
          v-show="showSwish"
          @close="closeSwish"
          class="donation-form__swish"
          :lead-id="form.leadId"
          :amount="form.amount"
          :labels="labels"
          :name="fullName"
          :phone="form.phone"
          :payment-id="paymentId"
        />
      </div>
    </div>
  </div>
</template>

<script>
import 'idempotent-babel-polyfill';

import MaskableInput from './MaskableInput';
import CurrencyInput from './CurrencyInput';
import donationMix from './mixin.js';
import Stripe from './Stripe';
import Swish from './Swish';
import BankId from './BankId';
import BankAccount from './BankAccount';

export default {
  mixins: [donationMix],

  name: 'Donation',

  components: {
    MaskableInput,
    CurrencyInput,
    Stripe,
    Swish,
    BankId,
    BankAccount,
  },

  props: {
    recurrentPaymentMethods: {
      type: Array,
      default: () => [],
    },
    simplePaymentMethods: {
      type: Array,
      default: () => [],
    },
    getBankAccountFromSocialSecurity: {
      type: Boolean,
      default: true,
    },
    getAddressShowForm: {
      type: Boolean,
      default: true,
    },
    allowAnonym: {
      type: Boolean,
      default: false,
    },
    useCompensation: {
      type: Boolean,
      default: false,
    },
    hideCustomAmount: {
      type: Boolean,
      default: false,
    },
    banks: {
      type: Array,
      default: () => [],
    },
    termsSimple: {
      type: String,
      default: null,
    },
    secondaryHeading: {
      type: String,
      default: null,
    },
    termsRecurrent: {
      type: String,
      default: null,
    },
    confirmationSimple: {
      type: String,
      default: null,
    },
    confirmationRecurrent: {
      type: String,
      default: null,
    },
    checkboxText: {
      type: String,
      default: null,
    },
    collection: {
      type: Array,
      default: null,
    },
    targetName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      validate: false,
      validEmail: false,
      validPhone: false,
      validSsn: false,
      validName: false,
      validSurname: false,
      validAdress: false,
      validZip: false,
      validCity: false,
      validClearing: false,
      validAccount: false,
      validPledge: false,
      scrolled: false,
      checked: false,
      form: {
        template: 'donation',
      },
    };
  },

  watch: {
    showStripe: function (value) {
      if (value) {
        setTimeout(() =>
          // eslint-disable-next-line prettier/prettier
          this.$scrollTo('#stripe-wrapper', 500, { offset: -370 }),
        );
      }
    },
    'form.method': function () {
      this.submitDisabled = false;
    },
  },

  mounted() {
    this.step = this.hasBothRecurrentAndSimple ? 1 : 2;
    this.formName = this.title;
    if (this.leadId !== null) {
      if (this.form.method === null) {
        this.form.method = this.availablePaymentMethods[0];
      }
      if (this.hasBothRecurrentAndSimple) {
        this.form.paymentType = 'recurrent';
      }
      this.step = 4;
    }
    if (this.isCompany) {
      this.validation = {
        socialSecurityNumber: /^[0-9]{6}[- ]?[0-9]{4}$/,
      };
    }
  },

  computed: {
    validationRegExpr() {
      return { ...this.defaultValidation, ...this.validation };
    },

    isAutogiro() {
      return this.form.method && this.form.method.split('|')[0] === 'autogiro';
    },

    autogiroType() {
      return this.isAutogiro ? this.form.method.split('|')[1] : null;
    },

    hasBothRecurrentAndSimple() {
      return (
        this.recurrentPaymentMethods.length > 0 &&
        this.simplePaymentMethods.length > 0
      );
    },

    isRecurrent() {
      return (
        this.simplePaymentMethods.length === 0 ||
        this.form.paymentType === 'recurrent'
      );
    },

    isSimple() {
      return (
        this.recurrentPaymentMethods.length === 0 ||
        this.form.paymentType === 'oneTime'
      );
    },

    availablePaymentMethods() {
      return this.isRecurrent
        ? this.recurrentPaymentMethods
        : this.simplePaymentMethods;
    },

    anonym() {
      return (
        this.allowAnonym &&
        (this.form.paymentType === 'oneTime' ||
          this.recurrentPaymentMethods.length === 0)
      );
    },

    stepEmailAndPaymentMethod() {
      return (
        (this.step > 1 && this.hasBothRecurrentAndSimple) ||
        (this.step > 0 && !this.hasBothRecurrentAndSimple)
      );
    },

    showForm() {
      return (
        ((this.step > 2 && this.getAddressShowForm === true) ||
          this.step > 3) &&
        this.form.anonym !== true
      );
    },

    showGetAddressWithSsn() {
      return (
        ((this.step > 1 && this.hasBothRecurrentAndSimple === false) ||
          this.step > 2) &&
        this.getAddressFromSocialSecurity === true &&
        this.form.anonym !== true
      );
    },

    image() {
      return this.collection && this.collection.image
        ? this.collection.image
        : this.background;
    },

    backgroundCSS() {
      return this.image && this.imagePosition == 0
        ? "background-image: url('" + this.image + "')"
        : null;
    },

    headCSS() {
      return this.image && this.imagePosition == 1
        ? "background-image: url('" + this.image + "')"
        : null;
    },

    returnUrl() {
      return this.isRecurrent
        ? this.confirmationRecurrent
        : this.confirmationSimple;
    },

    terms() {
      if (this.isRecurrent && this.termsRecurrent !== null) {
        return this.termsRecurrent;
      } else if (this.isSimple && this.termsSimple !== null) {
        return this.termsSimple;
      }
      return null;
    },

    paymentButtonLoading() {
      return (
        this.payexRedirectInProgress || this.bankIdSignature.status !== false
      );
    },

    hasStripe() {
      return this.simplePaymentMethods.indexOf('stripe') > -1;
    },

    hasSwish() {
      return this.simplePaymentMethods.indexOf('swish-direct') > -1;
    },
  },

  methods: {
    stepPassed(step) {
      switch (step) {
        case 1:
          if (
            (this.prefilled !== null || this.isValid('amount')) &&
            (this.isRecurrent || this.isSimple)
          ) {
            if (this.availablePaymentMethods.length > 1) {
              this.error = null;
              this.step = 2;
            } else {
              this.form.method = this.availablePaymentMethods[0];
              this.error = null;
              this.step = 3;
            }
          }
          if (
            this.hasBothRecurrentAndSimple === true &&
            this.form.anonym === true
          ) {
            this.form.anonym = false;
          }
          if (
            this.hasBothRecurrentAndSimple === true &&
            this.form.paymentType !== null &&
            this.scrolled === false
          ) {
            this.scrolled = true;
            this.$scrollTo('#anchor', 500, { offset: -100 });
          }
          break;
        case 2:
          this.validEmail = true;
          this.validPhone = true;
          if (
            this.form.method === null &&
            this.availablePaymentMethods.length === 1
          ) {
            this.form.method = this.availablePaymentMethods[0];
          }
          if (this.form.method === null) break;
          if (
            this.form.anonym === true ||
            (this.areValid(['email', 'phone']) &&
              (this.isValid('amount') || this.prefilled !== null))
          ) {
            this.error = null;
            this.step = 3;
          } else {
            if (
              (this.areValid('email') || this.areValid('phone')) &&
              this.isRecurrent === true &&
              this.recurrentPaymentMethods.length <= 1
            ) {
              this.error = null;
            } else {
              this.form.method = null;
              this.error = this.labels.errorRequiredFields;
            }
          }
          break;
        case 3:
          this.error = null;
          this.step = 4;
          break;
      }
    },

    submit() {
      this.validate = true;
      this.validEmail = true;
      this.validPhone = true;
      this.validSsn = true;
      this.validName = true;
      this.validSurname = true;
      this.validAdress = true;
      this.validZip = true;
      this.validCity = true;
      if (!this.useCompensation) {
        this.form.amount = this.prefilled
          ? this.prefilled
          : parseInt(this.amount);
      }
      if (this.form.amount === null || isNaN(this.form.amount)) {
        this.error = this.labels.errorNoAmount;
        return false;
      }
      if (this.form.amount < this.min) {
        this.error = this.labels.errorMinimum.replace(/_AMOUNT_/, this.min);
        return false;
      }
      if (
        this.form.method === null &&
        this.availablePaymentMethods.length === 1
      ) {
        this.form.method = this.availablePaymentMethods[0];
      }
      if (this.form.method === null) {
        this.error = this.labels.errorNoPaymentMethod;
        return false;
      }
      if (this.collection) {
        this.form.pledgeTarget = this.collection.targetId;
      }
      if (this.pledgeTargets && this.form.pledgeTarget === null) {
        this.checkValidation('pledgeTarget');
        this.error = this.labels.errorRequiredFields;
        return false;
      }
      if (
        this.isAutogiro &&
        this.checkboxText &&
        this.checkboxText !== '' &&
        this.checked === false
      ) {
        this.error = this.labels.errorCheckbox;
        return false;
      } else if (
        ['swish', 'creditcard', 'invoice', 'directdebit'].indexOf(
          // eslint-disable-next-line prettier/prettier
          this.form.method,
        ) > -1
      ) {
        this.message = this.labels.redirectPayex;
        return this.payex();
      } else if (this.isAutogiro) {
        if (
          this.missingFields([
            'firstName',
            'lastName',
            'address',
            'zip',
            'city',
            'phone',
            'email',
            'bank',
            'bankAccount',
            'bankClearing',
            'amount',
            'socialsecuritynumber',
          ])
        ) {
          return false;
        } else {
          return this.startBankIdSign();
        }
      } else if (this.form.method === 'stripe') {
        this.submitDisabled = true;
        this.showStripe = true;
        return this.saveLead();
      } else if (this.form.method === 'swish-direct') {
        this.swish();
      } else {
        this.error = this.labels.error;
      }
    },

    closeModal() {
      this.qr = null;
      this.bankAccountRetrieval.accounts = [];
    },

    selectBankAccountFromModal(account) {
      this.qr = null;
      this.form.bankAccount = account.AccountNo;
      this.form.bankClearing = account.ClearingNo;
      this.bankAccountRetrieval.accounts = [];
      this.bankAccountRetrieval.selected = true;
    },

    anonymStatusChanged() {
      this.step = 3;
      this.error = null;
      this.message = null;
      if (this.form.anonym === true) {
        this.form.firstName = null;
        this.form.lastName = null;
        this.form.address = null;
        this.form.address2 = null;
        this.form.phone2 = null;
        this.form.zip = null;
        this.form.city = null;
        this.form.socialSecurityNumber = null;
        this.form.phone = null;
        this.form.email = null;
      }
    },

    prefilledAmountChanged() {
      this.form.amount = this.prefilled;
      this.amount = null;
    },

    amountChanged() {
      this.form.amount = parseInt(this.amount);
      this.prefilled = null;
    },

    amountChangedCompensation(target, value) {
      const parsedValue = parseInt(value);
      if (target === 'car') {
        this.form.amount = parsedValue * 1.8;
        this.amount = parsedValue * 1.8;
        this.$refs.airPlane.value = null;
      } else if (target === 'airplane') {
        this.form.amount = parsedValue * 3.6;
        this.amount = parsedValue * 1.8;
        this.$refs.car.value = null;
      }
      this.prefilled = null;
    },

    makeId(base, id) {
      return base + id;
    },

    makeClass(method) {
      return 'donation__form-method-' + method;
    },

    saveUncompletedLead() {
      this.stepPassed(this.step);
      if (this.isRecurrent === true) {
        this.form.amount = this.prefilled ? this.prefilled : this.amount;
        this.saveLead();
      }
    },
  },
};
</script>

<style lang="scss">
.donation-form {
  text-align: center;
  margin: auto;
  max-width: 800px;

  input[type='number'],
  input[type='tel'] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &__error {
    color: red;
  }

  &__success {
    color: green;
  }

  &__form-control {
    margin: 10px;

    input,
    button {
      padding: 10px;
      width: 300px;
    }

    &-valid {
      border: 1px solid green;
    }

    &-error {
      border: 1px solid red;
    }
  }

  &__terms-toggle {
    font-size: 75%;
    text-decoration: underline;
  }

  &__fields {
    display: flex;
    justify-content: center;
    margin: 20px;
    &--field {
      flex: 0 0 auto;
      input[type='radio'] {
        position: absolute;
        left: -9999px;
      }
      label {
        padding: 10px;
        cursor: pointer;
        color: black;
      }
      input[type='radio']:checked + label {
        background: green;
        color: white;
      }
    }
  }
}
</style>
